import { applyUserMessageRailUpdate } from "@t3tools/shared/userMessageRail";
import { pipe } from "effect/Function";
import * as Order from "effect/Order";
import * as Arr from "effect/Array";
import * as DateTime from "effect/DateTime";
import * as Duration from "effect/Duration";
import * as Effect from "effect/Effect";
import * as Fiber from "effect/Fiber";
import { Atom } from "effect/unstable/reactivity";
import { applyLiveMessageFrame, clearLiveMessage, EMPTY_LIVE_MESSAGE_OVERLAY, liveMessageOverlayFromSnapshot, } from "./liveMessages.js";
import { DEFAULT_THREAD_DETAIL_LIMITS, applyThreadDetailEvent, } from "./threadDetailReducer.js";
const NOOP = () => undefined;
const nowMs = () => DateTime.toEpochMillis(DateTime.nowUnsafe());
function clearEntryEviction(entry) {
    if (entry.evictionFiber !== null) {
        Effect.runFork(Fiber.interrupt(entry.evictionFiber));
        entry.evictionFiber = null;
    }
}
export const EMPTY_THREAD_DETAIL_STATE = Object.freeze({
    userMessageRail: [],
    data: null,
    liveMessages: EMPTY_LIVE_MESSAGE_OVERLAY,
    error: null,
    isPending: false,
    isDeleted: false,
});
const INITIAL_THREAD_DETAIL_STATE = Object.freeze({
    userMessageRail: [],
    data: null,
    liveMessages: EMPTY_LIVE_MESSAGE_OVERLAY,
    error: null,
    isPending: true,
    isDeleted: false,
});
const knownThreadDetailKeys = new Set();
export const threadDetailStateAtom = Atom.family((key) => {
    knownThreadDetailKeys.add(key);
    return Atom.make(INITIAL_THREAD_DETAIL_STATE).pipe(Atom.keepAlive, Atom.withLabel(`thread-detail:${key}`));
});
export const EMPTY_THREAD_DETAIL_ATOM = Atom.make(EMPTY_THREAD_DETAIL_STATE).pipe(Atom.keepAlive, Atom.withLabel("thread-detail:null"));
export function getThreadDetailTargetKey(target) {
    if (target.environmentId === null || target.threadId === null) {
        return null;
    }
    return `${target.environmentId}:${target.threadId}`;
}
export function createThreadDetailManager(config) {
    const entries = new Map();
    function getSnapshot(target) {
        const targetKey = getThreadDetailTargetKey(target);
        if (targetKey === null) {
            return EMPTY_THREAD_DETAIL_STATE;
        }
        return config.getRegistry().get(threadDetailStateAtom(targetKey));
    }
    function setState(targetKey, nextState) {
        config.getRegistry().set(threadDetailStateAtom(targetKey), nextState);
        reconcileRetention(targetKey);
    }
    function markPending(targetKey) {
        const current = config.getRegistry().get(threadDetailStateAtom(targetKey));
        setState(targetKey, {
            ...current,
            error: null,
            isPending: true,
        });
    }
    function setData(targetKey, thread, liveMessages) {
        setState(targetKey, {
            userMessageRail: config.getRegistry().get(threadDetailStateAtom(targetKey)).userMessageRail,
            data: thread,
            liveMessages,
            error: null,
            isPending: false,
            isDeleted: false,
        });
    }
    function setDeleted(targetKey) {
        setState(targetKey, {
            userMessageRail: [],
            data: null,
            liveMessages: EMPTY_LIVE_MESSAGE_OVERLAY,
            error: null,
            isPending: false,
            isDeleted: true,
        });
    }
    function shouldKeepWarm(entry) {
        return config.retention?.shouldKeepWarm?.(entry.target, getSnapshot(entry.target)) ?? false;
    }
    function disposeEntry(targetKey) {
        const entry = entries.get(targetKey);
        if (!entry) {
            return;
        }
        clearEntryEviction(entry);
        entry.teardown();
        entries.delete(targetKey);
    }
    function evictIdleEntriesToCapacity() {
        const retention = config.retention;
        if (!retention || entries.size <= retention.maxRetainedEntries) {
            return;
        }
        const idleEntries = pipe(Arr.fromIterable(entries), Arr.filter(([, entry]) => entry.watcherCount === 0 && entry.retainCount === 0 && !shouldKeepWarm(entry)), Arr.sortWith(([, e]) => e.lastAccessedAt, Order.Number));
        for (const [targetKey] of idleEntries) {
            if (entries.size <= retention.maxRetainedEntries) {
                return;
            }
            disposeEntry(targetKey);
        }
    }
    function scheduleEviction(targetKey, entry) {
        const retention = config.retention;
        clearEntryEviction(entry);
        if (!retention) {
            disposeEntry(targetKey);
            return;
        }
        if (retention.idleTtlMs <= 0) {
            disposeEntry(targetKey);
            return;
        }
        entry.evictionFiber = Effect.runFork(Effect.sleep(Duration.millis(retention.idleTtlMs)).pipe(Effect.andThen(Effect.sync(() => {
            const current = entries.get(targetKey);
            if (!current) {
                return;
            }
            current.evictionFiber = null;
            if (current.watcherCount > 0 || current.retainCount > 0 || shouldKeepWarm(current)) {
                return;
            }
            disposeEntry(targetKey);
        }))));
    }
    function reconcileRetention(targetKey) {
        const entry = entries.get(targetKey);
        if (!entry) {
            return;
        }
        clearEntryEviction(entry);
        if (entry.watcherCount > 0 || entry.retainCount > 0 || shouldKeepWarm(entry)) {
            return;
        }
        scheduleEviction(targetKey, entry);
        evictIdleEntriesToCapacity();
    }
    function applyStreamItem(targetKey, item, threadId) {
        if (item.kind === "rail") {
            const current = config.getRegistry().get(threadDetailStateAtom(targetKey));
            setState(targetKey, {
                ...current,
                userMessageRail: applyUserMessageRailUpdate(current.userMessageRail, item.update),
            });
            return;
        }
        if (item.kind === "snapshot") {
            setData(targetKey, item.snapshot.thread, liveMessageOverlayFromSnapshot(item.snapshot.liveMessages));
            return;
        }
        const currentState = getSnapshot({
            environmentId: entries.get(targetKey)?.target.environmentId ?? null,
            threadId,
        });
        const current = currentState.data;
        if (current === null) {
            if (item.kind === "event" && item.event.type === "thread.deleted") {
                setDeleted(targetKey);
            }
            return;
        }
        if (item.kind === "live") {
            const liveMessages = applyLiveMessageFrame(currentState.liveMessages, item.live);
            if (liveMessages !== currentState.liveMessages) {
                setData(targetKey, current, liveMessages);
            }
            return;
        }
        const nextLiveMessages = item.event.type === "thread.message-sent" && !item.event.payload.streaming
            ? clearLiveMessage(currentState.liveMessages, item.event.payload.messageId)
            : currentState.liveMessages;
        const result = applyThreadDetailEvent(current, item.event, config.limits ?? DEFAULT_THREAD_DETAIL_LIMITS);
        if (result.kind === "updated") {
            setData(targetKey, result.thread, nextLiveMessages);
            return;
        }
        if (result.kind === "deleted") {
            setDeleted(targetKey);
        }
    }
    function subscribeStream(targetKey, target, client) {
        markPending(targetKey);
        return client.subscribeThread({ threadId: target.threadId }, (item) => applyStreamItem(targetKey, item, target.threadId), {
            onResubscribe: () => markPending(targetKey),
        });
    }
    function createDynamicSubscription(targetKey, target) {
        let currentIdentity = null;
        let currentUnsub = NOOP;
        const sync = () => {
            const client = config.getClient(target.environmentId);
            const identity = client
                ? (config.getClientIdentity?.(target.environmentId) ?? target.environmentId)
                : null;
            if (!client || identity === null) {
                if (currentIdentity !== null) {
                    currentUnsub();
                    currentUnsub = NOOP;
                    currentIdentity = null;
                }
                markPending(targetKey);
                return;
            }
            if (currentIdentity === identity) {
                return;
            }
            currentUnsub();
            currentIdentity = identity;
            currentUnsub = subscribeStream(targetKey, target, client);
        };
        const unsubChanges = config.subscribeClientChanges(sync);
        sync();
        return () => {
            unsubChanges();
            currentUnsub();
        };
    }
    function acquire(target, kind, client) {
        const targetKey = getThreadDetailTargetKey(target);
        if (targetKey === null || target.environmentId === null || target.threadId === null) {
            return NOOP;
        }
        const existing = entries.get(targetKey);
        if (existing) {
            clearEntryEviction(existing);
            existing.lastAccessedAt = nowMs();
            if (kind === "watcher") {
                existing.watcherCount += 1;
            }
            else {
                existing.retainCount += 1;
            }
            return () => release(targetKey, kind);
        }
        let teardown;
        const resolvedTarget = {
            environmentId: target.environmentId,
            threadId: target.threadId,
        };
        if (client) {
            teardown = subscribeStream(targetKey, resolvedTarget, client);
        }
        else if (config.subscribeClientChanges) {
            teardown = createDynamicSubscription(targetKey, resolvedTarget);
        }
        else {
            const resolved = config.getClient(target.environmentId);
            if (!resolved) {
                return NOOP;
            }
            teardown = subscribeStream(targetKey, resolvedTarget, resolved);
        }
        entries.set(targetKey, {
            target: resolvedTarget,
            watcherCount: kind === "watcher" ? 1 : 0,
            retainCount: kind === "retain" ? 1 : 0,
            teardown,
            lastAccessedAt: nowMs(),
            evictionFiber: null,
        });
        evictIdleEntriesToCapacity();
        return () => release(targetKey, kind);
    }
    function release(targetKey, kind) {
        const entry = entries.get(targetKey);
        if (!entry) {
            return;
        }
        if (kind === "watcher") {
            entry.watcherCount = Math.max(0, entry.watcherCount - 1);
        }
        else {
            entry.retainCount = Math.max(0, entry.retainCount - 1);
        }
        entry.lastAccessedAt = nowMs();
        reconcileRetention(targetKey);
    }
    function watch(target, client) {
        return acquire(target, "watcher", client);
    }
    function retain(target, client) {
        return acquire(target, "retain", client);
    }
    function invalidate(target) {
        if (target) {
            const targetKey = getThreadDetailTargetKey(target);
            if (targetKey !== null) {
                disposeEntry(targetKey);
                config.getRegistry().set(threadDetailStateAtom(targetKey), EMPTY_THREAD_DETAIL_STATE);
            }
            return;
        }
        for (const targetKey of entries.keys()) {
            disposeEntry(targetKey);
        }
        for (const key of knownThreadDetailKeys) {
            config.getRegistry().set(threadDetailStateAtom(key), EMPTY_THREAD_DETAIL_STATE);
        }
    }
    function reset() {
        invalidate();
    }
    return {
        watch,
        retain,
        getSnapshot,
        invalidate,
        reset,
    };
}
